import { useState } from "react";

import { Box, Typography, TextField, IconButton, Chip, CircularProgress } from "@mui/material";
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import { toast } from "react-toastify";

import { useGetStaffFailureAlertRecipientsQuery } from "api/staff/queries/useGetStaffFailureAlertRecipientsQuery";
import { useAddStaffFailureAlertRecipientMutation } from "api/staff/mutations/useAddStaffFailureAlertRecipientMutation";
import { useRemoveStaffFailureAlertRecipientMutation } from "api/staff/mutations/useRemoveStaffFailureAlertRecipientMutation";

const StaffFailureAlertRecipients = () => {
  const [newEmail, setNewEmail] = useState("");

  const { data: recipients = [], isLoading, refetch } = useGetStaffFailureAlertRecipientsQuery();
  const addMutation = useAddStaffFailureAlertRecipientMutation();
  const removeMutation = useRemoveStaffFailureAlertRecipientMutation();

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;

    try {
      await addMutation.mutateAsync({ email: newEmail.trim() });
      toast.success("Email added successfully");
      setNewEmail("");
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to add email");
    }
  };

  const handleRemoveEmail = async (email: string) => {
    try {
      await removeMutation.mutateAsync({ email });
      toast.success("Email removed successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to remove email");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  if (isLoading) {
    return (
      <Box sx={styles.container}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Typography variant="body2" color="text.secondary" mb={2} fontWeight={600}>
        Staff Failure Alert Recipients
      </Typography>

      <Box sx={styles.addEmailContainer}>
        <TextField
          fullWidth
          size="small"
          placeholder="Add email address..."
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={styles.textField}
        />
        <IconButton
          onClick={handleAddEmail}
          disabled={!newEmail.trim() || addMutation.isPending}
          sx={styles.addButton}
        >
          <AddIcon />
        </IconButton>
      </Box>

      <Box sx={styles.recipientsList}>
        {recipients.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={styles.emptyText}>
            No recipients configured
          </Typography>
        ) : (
          recipients.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => handleRemoveEmail(email)}
              deleteIcon={<CloseIcon />}
              sx={styles.chip}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    mb: 4,
  },
  addEmailContainer: {
    display: "flex",
    gap: 1,
    alignItems: "center",
    mb: 2,
  },
  textField: {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.1)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(255, 255, 255, 0.3)",
      },
    },
    "& .MuiInputBase-input": {
      color: "text.primary",
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
    },
  },
  addButton: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: "text.primary",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      color: "text.disabled",
    },
  },
  recipientsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
    minHeight: 40,
  },
  chip: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: "text.primary",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
    },
    "& .MuiChip-deleteIcon": {
      color: "text.secondary",
      "&:hover": {
        color: "text.primary",
      },
    },
  },
  emptyText: {
    fontStyle: "italic",
    opacity: 0.6,
  },
};

export default StaffFailureAlertRecipients;
